import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { arrayUnion, doc, getDoc, updateDoc } from 'firebase/firestore'
import { onAuthStateChanged } from 'firebase/auth'
import { auth, db } from '../firebase'
import SectionAd from '../components/ads/SectionAd'

const RosterInvite = () => {
  const { rosterId } = useParams()
  const navigate = useNavigate()
  const [roster, setRoster] = useState(null)
  const [user, setUser] = useState(null)
  const [initialLoading, setInitialLoading] = useState(true)
  const [joinLoading, setJoinLoading] = useState(false)
  const [checkingMembership, setCheckingMembership] = useState(true)
  const [isMember, setIsMember] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')

  const setFeedback = useCallback((type, text) => {
    setMessageType(type)
    setMessage(text)
  }, [])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, currentUser => {
      setUser(currentUser)
    })

    return () => unsubscribe()
  }, [])

  // 🔧 FUNZIONI SPOSTATE QUI - PRIMA DI ESSERE UTILIZZATE
  const getMonthNumber = monthName => {
    const months = {
      gennaio: 0,
      febbraio: 1,
      marzo: 2,
      aprile: 3,
      maggio: 4,
      giugno: 5,
      luglio: 6,
      agosto: 7,
      settembre: 8,
      ottobre: 9,
      novembre: 10,
      dicembre: 11
    }
    return months[monthName?.toLowerCase()] || 0
  }

  const formatDate = dateValue => {
    if (!dateValue) return 'Non definita'

    try {
      let date

      // Firestore Timestamp
      if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate()
      }
      // Oggetto con seconds (Firestore)
      else if (dateValue?.seconds) {
        date = new Date(dateValue.seconds * 1000)
      }
      // Stringa o numero
      else {
        date = new Date(dateValue)
      }

      // Verifica validità
      if (isNaN(date.getTime())) {
        return 'Data non valida'
      }

      // Restituisce solo giorno/mese/anno
      return date.toLocaleDateString('it-IT', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    } catch (error) {
      console.error('Errore formatDate:', error)
      return 'Data non valida'
    }
  }

  useEffect(() => {
    const fetchRoster = async () => {
      setInitialLoading(true)
      try {
        const rosterRef = doc(db, 'rosters', rosterId)
        const rosterSnap = await getDoc(rosterRef)

        if (!rosterSnap.exists()) {
          setFeedback('error', 'Roster non trovato.')
          setRoster(null)
          return
        }

        setRoster({ id: rosterSnap.id, ...rosterSnap.data() })
        setFeedback('', '')
      } catch (error) {
        console.error('Errore nel caricamento del roster:', error)
        setFeedback(
          'error',
          'Impossibile caricare il roster. Riprova piu tardi.'
        )
      } finally {
        setInitialLoading(false)
      }
    }

    fetchRoster()
  }, [rosterId, setFeedback])

  const checkIfMember = useCallback(
    async currentUser => {
      if (!currentUser || !rosterId) {
        setIsMember(false)
        setCheckingMembership(false)
        return
      }

      setCheckingMembership(true)
      try {
        const userRef = doc(db, 'users', currentUser.uid)
        const userSnap = await getDoc(userRef)
        const rosterRef = doc(db, 'rosters', rosterId)
        const rosterSnap = await getDoc(rosterRef)

        const userRosterIds = userSnap.exists()
          ? userSnap.data().rosterIds || []
          : []
        const rosterParticipantIds = rosterSnap.exists()
          ? rosterSnap.data().participantIds || []
          : []

        setIsMember(
          userRosterIds.includes(rosterId) ||
          rosterParticipantIds.includes(currentUser.uid)
        )
      } catch (error) {
        console.error('Errore nel controllo della membership:', error)
        setFeedback(
          'error',
          'Impossibile verificare l invito in questo momento.'
        )
      } finally {
        setCheckingMembership(false)
      }
    },
    [rosterId, setFeedback]
  )

  useEffect(() => {
    checkIfMember(user)
  }, [user, checkIfMember])

  const handleJoinRoster = async () => {
    if (!user) {
      setFeedback('error', "Devi effettuare l'accesso per unirti a un roster.")
      return
    }

    setJoinLoading(true)
    setFeedback('', '')

    try {
      const rosterRef = doc(db, 'rosters', rosterId)
      const userRef = doc(db, 'users', user.uid)

      await Promise.all([
        updateDoc(rosterRef, {
          participantIds: arrayUnion(user.uid)
        }),
        updateDoc(userRef, {
          rosterIds: arrayUnion(rosterId)
        })
      ])

      setIsMember(true)
      setFeedback(
        'success',
        `Invito accettato. Ora fai parte di ${roster?.name || 'questo roster'}.`
      )

      setTimeout(() => {
        navigate('/profile')
      }, 900)
    } catch (error) {
      console.error("Errore durante l'unione al roster:", error)
      setFeedback(
        'error',
        'Si e verificato un errore durante l accesso al roster. Riprova piu tardi.'
      )
    } finally {
      setJoinLoading(false)
    }
  }

  const rosterStats = useMemo(() => {
    if (!roster) return []

    return [
      {
        icon: 'bi-controller',
        label: 'Modalità',
        value: roster.gameType || 'Standard'
      },
      {
        icon: 'bi-calendar-event',
        label: 'Inizio',
        value: formatDate(roster.dataInizioPunteggio)
      },
      {
        icon: 'bi-person-badge',
        label: 'Wrestler',
        value: roster.maxWrestlers || 6
      },
      {
        icon: 'bi-coin',
        label: 'Crediti',
        value: roster.maxCredits || (roster.maxWrestlers || 6) * 50
      }
    ]
  }, [roster])

  const getGradient = gameType => {
    switch (gameType) {
      case 'Indipendente':
        return 'linear-gradient(135deg, rgba(146, 201, 252, 0.22) 0%, rgba(38, 56, 72, 0.96) 42%, rgba(18, 24, 31, 0.98) 100%)'
      case 'Competitivo':
        return 'linear-gradient(135deg, rgba(216, 185, 130, 0.22) 0%, rgba(86, 62, 23, 0.94) 38%, rgba(18, 24, 31, 0.98) 100%)'
      case 'Road To':
      case 'Road to WrestleMania':
        return 'linear-gradient(135deg, rgba(222, 58, 71, 0.2) 0%, rgba(110, 23, 30, 0.95) 40%, rgba(18, 24, 31, 0.98) 100%)'
      default:
        return 'linear-gradient(135deg, rgba(146, 201, 252, 0.18) 0%, rgba(38, 56, 72, 0.95) 40%, rgba(18, 24, 31, 0.98) 100%)'
    }
  }

  const feedbackAlertClass =
    messageType === 'success' ? 'alert alert-success' : 'alert alert-danger'

  if (initialLoading || checkingMembership) {
    return (
      <div className='container py-5'>
        <div
          className='card border-brand-secondary bg-brand-dark text-white shadow-lg mx-auto'
          style={{ maxWidth: '720px' }}
        >
          <div className='card-body text-center py-5'>
            <div
              className='spinner-border text-brand-secondary mb-3'
              role='status'
              aria-hidden='true'
            />
            <p className='mb-0 text-muted-brand'>
              Stiamo preparando il tuo invito al roster...
            </p>
          </div>
        </div>
      </div>
    )
  }

  const renderStatusBlock = () => {
    if (!roster) {
      return (
        <div className='text-center'>
          <div className='rounded-4 p-4 warning border border-brand-accent'>
            <i className='bi bi-exclamation-triangle-fill text-brand-accent fs-1 d-block mb-3' />
            <h2 className='section-title text-white mb-2'>
              Invito non disponibile
            </h2>
            <p className='text-muted-brand mb-4'>
              Il roster che stai cercando non esiste o non e piu accessibile.
            </p>
          </div>
          <button
            type='button'
            className='btn btn-outline-brand-secondary mt-3'
            onClick={() => navigate('/')}
          >
            Torna alla home
          </button>
        </div>
      )
    }

    if (!user) {
      return (
        <div>
          <span className='badge brand-badge mb-3'>Invito attivo</span>
          <h1 className='brand-title mb-3'>
            Accedi per entrare in {roster.name}
          </h1>
          <p className='brand-subtitle mb-4'>
            Hai ricevuto un invito a questo roster. Effettua l&apos;accesso o
            crea un account per accettarlo e iniziare subito a partecipare.
          </p>

          <div className='d-grid gap-2 d-sm-flex'>
            <button
              type='button'
              className='btn btn-brand-secondary'
              onClick={() => navigate('/auth')}
            >
              <i className='bi bi-box-arrow-in-right me-2' />
              Accedi o registrati
            </button>
            <Link to='/rules' className='btn btn-outline-brand-secondary'>
              <i className='bi bi-journal-text me-2' />
              Leggi il regolamento
            </Link>
          </div>
        </div>
      )
    }

    if (isMember) {
      return (
        <div className='text-center text-sm-start'>
          <div className='rounded-4 p-4 bg-brand-primary-light border border-brand-secondary mb-4'>
            <h2 className='text-white mb-2'>
              <i className='bi bi-patch-check-fill text-brand-secondary fs-1 me-3' />
              Sei gia dentro
            </h2>
            <p className='text-muted-brand mb-0'>
              Il tuo account e gia associato a questo roster. Puoi continuare
              dal profilo.
            </p>
          </div>
          <button
            type='button'
            className='btn btn-brand-secondary w-100'
            onClick={() => navigate('/profile')}
          >
            Vai al profilo
          </button>
        </div>
      )
    }

    return (
      <div>
        <h1 className='brand-title text-light mb-3'>
          Unisciti al roster{' '}
          <span className='fw-bold text-brand-secondary'>{roster.name}</span>
        </h1>
        <p className='brand-subtitle mb-0'>
          Accetta l'invito per entrare tra i partecipanti e sbloccare la
          gestione della tua fazione direttamente dal profilo.
        </p>
      </div>
    )
  }

  return (
    <div className='container py-4 py-md-5'>
      <div
        className='card border-0 rounded-4 overflow-hidden shadow-sm position-relative'
        style={{
          background: roster?.imageUrl
            ? `url(${roster.imageUrl}) center/cover no-repeat`
            : 'none',
          backgroundColor: roster?.imageUrl ? 'transparent' : 'var(--bs-dark)',
          maxWidth: '1100px',
          margin: '0 auto'
        }}
      >
        {/* Overlay con gradiente per effetto visivo e leggibilità */}
        <div
          className='position-absolute top-0 start-0 w-100 h-100'
          style={{
            background: roster?.gameType
              ? getGradient(roster.gameType)
              : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            opacity: 0.85,
            pointerEvents: 'none'
          }}
        ></div>

        <div
          className='card-body p-4 p-md-5 position-relative'
          style={{ zIndex: 2 }}
        >
          {message && (
            <div
              className={`${feedbackAlertClass} mb-4 rounded-4 border-0`}
              role='alert'
              style={{
                backdropFilter: 'blur(8px)',
                backgroundColor:
                  messageType === 'success'
                    ? 'rgba(40, 167, 69, 0.9)'
                    : 'rgba(220, 53, 69, 0.9)'
              }}
            >
              <i
                className={`bi ${messageType === 'success'
                    ? 'bi-check-circle-fill'
                    : 'bi-exclamation-circle-fill'
                  } me-2`}
              />
              {message}
            </div>
          )}

          <div className='row g-4 align-items-stretch'>
            <div className='col-12 col-lg-7'>{renderStatusBlock()}</div>

            {roster && (
              <div className='col-12 col-lg-5'>
                <div
                  className='h-100 p-3 rounded-4'
                  style={{
                    backdropFilter: 'blur(12px)'
                  }}
                >
                  <div className='row g-2'>
                    {rosterStats.map(stat => (
                      <div className='col-6' key={stat.label}>
                        <div
                          className='p-2 rounded-3 d-flex align-items-center gap-2'
                          style={{
                            background: 'rgba(0, 0, 0, 0.2)',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background =
                              'rgba(255, 255, 255, 0.1)'
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background =
                              'rgba(0, 0, 0, 0.2)'
                          }}
                        >
                          <i
                            className={`bi ${stat.icon} text-brand-secondary`}
                            style={{ fontSize: '1.25rem' }}
                          />
                          <div className='d-flex flex-column'>
                            <span
                              className='small text-white-50'
                              style={{ fontSize: '0.7rem' }}
                            >
                              {stat.label}
                            </span>
                            <strong
                              className='text-white'
                              style={{ fontSize: '1.1rem', lineHeight: 1.2 }}
                            >
                              {stat.value}
                            </strong>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bottone accetta invito - spostato fuori dalla row */}
          {roster && !isMember && user && (
            <div className='mt-4'>
              <div className='d-flex justify-content-between align-items-center gap-2'>
                <button
                  type='button'
                  className='btn btn-brand-secondary w-100'
                  onClick={handleJoinRoster}
                  disabled={joinLoading}
                >
                  {joinLoading ? (
                    <>
                      <span
                        className='spinner-border spinner-border-sm me-2'
                        role='status'
                        aria-hidden='true'
                      />
                      Accesso in corso...
                    </>
                  ) : (
                    <>
                      <i className='bi bi-person-plus-fill me-2' />
                      Accetta invito
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <SectionAd placement='homeBottom' className='d-flex justify-content-center mt-4 mb-0'/>
    </div>
  )
}

export default RosterInvite
